/**
 * File validation utilities
 *
 * Centralized file validation logic with configurable rules.
 * Provides consistent validation across all upload components.
 */

export type FileValidationConfigT = {
  maxSizeBytes: number;
  allowedTypes: string[];
  typeErrorMessage: string;
  sizeErrorMessage: string;
  unsupportedTypeMessage: string;
};

/**
 * Default configuration for image file validation
 *
 * Note: maxSizeBytes is set higher to allow for client-side compression.
 * The actual upload limit will be enforced by compression utilities.
 */
export const IMAGE_VALIDATION_CONFIG: FileValidationConfigT = {
  maxSizeBytes: 10 * 1024 * 1024, // 10MB (will be compressed to 500KB)
  allowedTypes: [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
    "image/webp",
    "image/svg+xml",
  ],
  typeErrorMessage:
    "Bitte wählen Sie eine gültige Bilddatei aus (PNG, JPG, GIF, WEBP, SVG)",
  sizeErrorMessage: "Die Datei ist zu groß. Maximale Dateigröße: 10MB",
  unsupportedTypeMessage:
    "Unsupported file type. Please use PNG, JPG, GIF, WEBP, or SVG",
};

/**
 * Configuration for compressed image validation
 * This is used after compression to ensure final file meets requirements
 */
export const COMPRESSED_IMAGE_VALIDATION_CONFIG: FileValidationConfigT = {
  maxSizeBytes: 500 * 1024, // 500KB
  allowedTypes: ["image/jpeg", "image/jpg", "image/png", "image/webp"],
  typeErrorMessage:
    "Bitte wählen Sie eine gültige Bilddatei aus (PNG, JPG, WEBP)",
  sizeErrorMessage:
    "Die komprimierte Datei ist immer noch zu groß. Bitte wählen Sie ein kleineres Bild.",
  unsupportedTypeMessage:
    "Unsupported file type after compression. Please use PNG, JPG, or WEBP",
};

/**
 * Configuration for document file validation (images + PDFs)
 *
 * Accepts both images and PDF documents for flexible document uploads.
 * Larger size limit for PDFs, same as images for consistency.
 */
export const DOCUMENT_VALIDATION_CONFIG: FileValidationConfigT = {
  maxSizeBytes: 10 * 1024 * 1024, // 10MB
  allowedTypes: [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
    "image/webp",
    "image/svg+xml",
    "application/pdf",
  ],
  typeErrorMessage:
    "Bitte wählen Sie eine gültige Datei aus (PNG, JPG, GIF, WEBP, SVG oder PDF)",
  sizeErrorMessage: "Die Datei ist zu groß. Maximale Dateigröße: 10MB",
  unsupportedTypeMessage:
    "Unsupported file type. Please use PNG, JPG, GIF, WEBP, SVG, or PDF",
};

/**
 * Validates a file against the provided configuration
 *
 * @param file - The file to validate
 * @param config - Validation configuration (defaults to IMAGE_VALIDATION_CONFIG)
 * @returns Error message if validation fails, null if valid
 *
 * @example
 * ```typescript
 * const error = validateFile(selectedFile);
 * if (error) {
 *   toast.error(error);
 *   return;
 * }
 * // File is valid, proceed with upload
 * ```
 */
export function validateFile(
  file: File,
  config: FileValidationConfigT = IMAGE_VALIDATION_CONFIG
): string | null {
  // Check file size
  if (file.size > config.maxSizeBytes) {
    return config.sizeErrorMessage;
  }

  // Check specific allowed types
  if (!config.allowedTypes.includes(file.type)) {
    return config.unsupportedTypeMessage;
  }

  return null;
}

/**
 * Formats file size in human readable format
 *
 * @param bytes - File size in bytes
 * @returns Formatted size string
 *
 * @example
 * ```typescript
 * formatFileSize(1024) // "1 KB"
 * formatFileSize(1048576) // "1 MB"
 * ```
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

/**
 * Checks if a file type is supported for upload
 *
 * @param fileType - MIME type of the file
 * @param config - Validation configuration
 * @returns true if file type is supported
 */
export function isFileTypeSupported(
  fileType: string,
  config: FileValidationConfigT = IMAGE_VALIDATION_CONFIG
): boolean {
  return config.allowedTypes.includes(fileType);
}

/**
 * Validates and optionally compresses an image file
 *
 * This function combines validation with compression for a seamless upload experience.
 * It validates the original file, compresses if needed, and validates the result.
 *
 * @param file - The image file to validate and compress
 * @param compress - Whether to compress the file if it's too large (default: true)
 * @returns Promise with validation result and optionally compressed file
 *
 * @example
 * ```typescript
 * const result = await validateAndCompressImage(selectedFile);
 * if (result.success) {
 *   // Use result.file for upload (compressed if needed)
 *   if (result.wasCompressed) {
 *     console.log(`Compressed from ${result.originalSize} to ${result.compressedSize} bytes`);
 *   }
 * }
 * ```
 */
export async function validateAndCompressImage(
  file: File,
  compress: boolean = true
): Promise<{
  success: boolean;
  file?: File;
  error?: string;
  wasCompressed?: boolean;
  originalSize?: number;
  compressedSize?: number;
  compressionRatio?: number;
}> {
  // First validate the original file
  const validationError = validateFile(file, IMAGE_VALIDATION_CONFIG);
  if (validationError) {
    return {
      success: false,
      error: validationError,
    };
  }

  // If compression is disabled or file is already small enough, return as-is
  if (
    !compress ||
    file.size <= COMPRESSED_IMAGE_VALIDATION_CONFIG.maxSizeBytes
  ) {
    return {
      success: true,
      file,
      wasCompressed: false,
      originalSize: file.size,
      compressedSize: file.size,
      compressionRatio: 1,
    };
  }

  // Import compression function dynamically to avoid circular dependencies
  const { compressImage } = await import("./image-compression");

  // Compress the image
  const compressionResult = await compressImage(file);

  if (!compressionResult.success) {
    return {
      success: false,
      error: compressionResult.error || "Komprimierung fehlgeschlagen",
    };
  }

  // Validate the compressed file
  const compressedValidationError = validateFile(
    compressionResult.file!,
    COMPRESSED_IMAGE_VALIDATION_CONFIG
  );

  if (compressedValidationError) {
    return {
      success: false,
      error: compressedValidationError,
    };
  }

  return {
    success: true,
    file: compressionResult.file,
    wasCompressed: true,
    originalSize: compressionResult.originalSize,
    compressedSize: compressionResult.compressedSize,
    compressionRatio: compressionResult.compressionRatio,
  };
}
